import { jwtDecode } from 'jwt-decode';
import { ZitadelAccessTokenPayload, ZitadelIdTokenPayload } from './zitadel-payload.interfaces';
import { ZitadelRole, ZitadelRoleEnum } from './zitadel-roles.enum';

export function getAccessTokenFromHeader(header: string | undefined) {
  if (!header) {
    return null;
  }
  return header.split(' ')[1];
}

export function decodeTokenToAccessToken(token: string) {
  return jwtDecode(token) as ZitadelAccessTokenPayload;
}

export function decodeTokenToIdToken(token: string) {
  return jwtDecode(token) as ZitadelIdTokenPayload;
}

export function decodeBase64(base64: string) {
  return Buffer.from(base64, 'base64').toString('utf-8');
}

/**
 * Read the roles from the Zitadel access token and return them as an array of strings
 */
export function getZitadelRoles(
  zitadelAccessTokenPayload: ZitadelAccessTokenPayload | null,
): ZitadelRole[] {
  const rolesForOrg: ZitadelRole[] = [];
  if (!zitadelAccessTokenPayload) {
    return rolesForOrg;
  }
  const orgId = getZitadelOrgId(zitadelAccessTokenPayload);
  const rolesInToken = zitadelAccessTokenPayload[`urn:zitadel:iam:org:project:roles`];
  if (rolesInToken === undefined) {
    return rolesForOrg;
  }
  const allRoles: ZitadelRole[] = Object.keys(rolesInToken) as ZitadelRole[];
  for (const role of allRoles) {
    if (rolesInToken[role][orgId]) {
      rolesForOrg.push(role);
    }
  }
  return rolesForOrg;
}

/**
 * Read the user ID from the Zitadel access token
 */
export function getZitadelUserId(zitadelAccessTokenPayload: ZitadelAccessTokenPayload): string {
  return zitadelAccessTokenPayload['sub'];
}

/**
 * Read the org ID from the Zitadel access token
 */
export function getZitadelOrgId(zitadelAccessTokenPayload: ZitadelAccessTokenPayload): string {
  return zitadelAccessTokenPayload['urn:zitadel:iam:org:id'];
}

/**
 * Check if a role is present in the Zitadel access token
 */
export function checkRoleInToken(
  zitadelAccessTokenPayload: ZitadelAccessTokenPayload | null,
  role: ZitadelRoleEnum,
): boolean {
  const roles = getZitadelRoles(zitadelAccessTokenPayload);
  return roles.includes(role);
}

/**
 * Get all scopes in the format of `urn:zitadel:iam:org:project:role:${role}`
 */
export function createAllScopes(): string[] {
  return Object.values(ZitadelRoleEnum).map((role) => `urn:zitadel:iam:org:project:role:${role}`);
}

/**
 * Get array of possible roles
 */
export function getPossibleRoles(): ZitadelRole[] {
  return Object.values(ZitadelRoleEnum) as ZitadelRole[];
}
